import React, {useCallback} from "react";

import {
  faCrown,
  faExclamationTriangle,
  faHourglassHalf,
  faShareNodes,
  faSignOutAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useUser} from "~/api/hooks/useUser";
import CustomButton from "~/component/common/CustomButton";
import {useGroup} from "~/contexts/group/GroupProvider";
import {useGroupSession} from "~/contexts/session/GroupSessionProvider";

const PlayerListItem: React.FC<{uid: string; isHost: boolean}> = ({
  uid,
  isHost,
}) => {
  const {data: user} = useUser({id: uid});
  if (!user)
    return <div className="h-12 bg-gray-800 rounded-lg animate-pulse" />;
  return (
    <div className="flex items-center p-2 bg-gray-800 rounded-lg">
      <img
        src={`https://api.dicebear.com/7.x/pixel-art/svg?seed=${user.name}`}
        alt={user.name}
        className="w-8 h-8 rounded-full mr-3"
      />
      <span className="text-white font-medium">{user.name}</span>
      {isHost && (
        <FontAwesomeIcon
          icon={faCrown}
          className="ml-auto text-yellow-400"
          title="Host"
        />
      )}
    </div>
  );
};

// --- A new helper component for centered status messages ---
const StatusDisplay: React.FC<{
  icon: any;
  title: string;
  message: string;
  spin?: boolean;
}> = ({icon, title, message, spin = false}) => (
  <div className="flex flex-col items-center justify-center text-center p-8 h-64">
    <FontAwesomeIcon
      icon={icon}
      spin={spin}
      className="text-gray-500 text-5xl mb-6"
    />
    <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400">{message}</p>
  </div>
);

// --- ManageGroupView now consumes the context directly and has no props ---
const ManageGroupView: React.FC = () => {
  const {leaveGroup, groupId} = useGroup();
  // The hook now provides the new status field
  const {status, sessionData, error} = useGroupSession();

  const handleInvite = useCallback(async () => {
    if (!groupId) return;
    const inviteUrl = `${window.location.origin}/?joinGroup=${groupId}`;
    const shareData = {
      title: "Orbit Dash Invite",
      text: "Join my group in Orbit Dash and let's play!",
      url: inviteUrl,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      await navigator
        .share(shareData)
        .catch((err) => console.error("Share failed:", err));
    } else {
      await navigator.clipboard
        .writeText(inviteUrl)
        .then(() => {
          alert(`Invite link copied!\n${inviteUrl}`);
        })
        .catch((err) => {
          console.error("Copy failed:", err);
          alert("Failed to copy link.");
        });
    }
  }, [groupId]);

  const handleLeave = () => {
    if (window.confirm("Are you sure you want to leave the group?")) {
      leaveGroup();
    }
  };

  // --- STATE 1: LOADING ---
  if (status === "LOADING") {
    return (
      <StatusDisplay
        icon={faSpinner}
        title="Connecting..."
        message="Joining the group session."
        spin={true}
      />
    );
  }

  // --- STATE 2: WAITING FOR HOST ---
  // This is only relevant for clients. The host would be in a loading/error state.
  if (status === "WAITING_FOR_HOST") {
    return (
      <div className="text-center p-8">
        <StatusDisplay
          icon={faHourglassHalf}
          title="Waiting for Host"
          message="The session will resume when the host reconnects."
        />
        {/* Still allow the user to leave while waiting */}
        <CustomButton
          onClick={handleLeave}
          className="w-full mt-4 bg-red-600/90 hover:bg-red-500/90"
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" /> Leave Group
        </CustomButton>
      </div>
    );
  }

  // --- STATE 3: ERROR ---
  if (status === "ERROR") {
    return (
      <div className="text-center p-8">
        <StatusDisplay
          icon={faExclamationTriangle}
          title="Connection Error"
          message={error || "An unknown error occurred."}
        />
        <CustomButton
          onClick={leaveGroup}
          className="w-full mt-4 bg-gray-600/90 hover:bg-gray-500/90"
        >
          Return to Lobby
        </CustomButton>
      </div>
    );
  }

  // --- STATE 4: SESSION ACTIVE ---
  // If we get here, status must be 'SESSION_ACTIVE' and sessionData must exist.
  if (status === "SESSION_ACTIVE" && sessionData) {
    const playerIds = Object.keys(sessionData.players);

    return (
      <div className="w-full">
        <h3 className="text-lg font-semibold text-gray-300 mb-3">
          Players in Group ({playerIds.length})
        </h3>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {playerIds.map((uid) => (
            <PlayerListItem
              key={uid}
              uid={uid}
              isHost={uid === sessionData.hostId}
            />
          ))}
        </div>
        <div className="mt-8 space-y-3">
          <CustomButton
            onClick={handleInvite}
            className="w-full rounded-full bg-gradient-to-br from-green-600 via-green-500 hover:via-green-400 to-black"
          >
            <FontAwesomeIcon icon={faShareNodes} className="mr-2" /> Share
            Invite
          </CustomButton>
          <CustomButton
            onClick={handleLeave}
            className="w-full rounded-full bg-gradient-to-br from-red-600 via-red-500 hover:via-red-400 to-black"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" /> Leave Group
          </CustomButton>
        </div>
      </div>
    );
  }

  // Fallback case, should not be reached if logic is correct.
  return null;
};

export default ManageGroupView;
